/* Vesper Cocktails redesign: site behaviour (header + product card).
   ES module, loaded from layout/theme.liquid. All handlers are delegated
   on document so they survive Horizon's section morphing and theme-editor
   re-renders. State lives on <html> classes for the same reason:
     html.vc-over-hero  header overlays a hero (transparent until scrolled)
     html.vc-scrolled   window.scrollY > 60 (SiteNav.dc.html threshold)
     html.vc-menu-open  full-page mobile menu panel open */

const root = document.documentElement;
const SCROLL_Y = 60;

let Events = null;
import('@theme/events').then((m) => { Events = m; }).catch(() => {});

/* ---------- Header ---------- */
function syncOverHero() {
  const header = document.querySelector('[data-vc-header]');
  if (!header) { root.classList.remove('vc-over-hero'); return; }
  const want = header.dataset.overHero === 'true' || !!document.querySelector('[data-vc-hero]');
  root.classList.toggle('vc-over-hero', want);
}

function onScroll() {
  root.classList.toggle('vc-scrolled', window.scrollY > SCROLL_Y);
}

function setMenu(open) {
  const was = root.classList.contains('vc-menu-open');
  root.classList.toggle('vc-menu-open', open);
  document.querySelectorAll('[data-vc-menu-toggle]').forEach((b) => b.setAttribute('aria-expanded', String(open)));
  if (open === was) return;
  const focusTo = open
    ? document.querySelector('[data-vc-menu-panel] .vc-nav__close')
    : document.querySelector('.vc-nav__menu-btn');
  if (focusTo) setTimeout(() => focusTo.focus({ preventScroll: true }), open ? 420 : 0);
}

function syncHeaderHeight() {
  const section = document.querySelector('.vc-header-section');
  if (!section) return;
  const h = Math.round(section.getBoundingClientRect().height);
  root.style.setProperty('--vc-header-height', h + 'px');
  root.style.setProperty('--header-height', h + 'px');
}

document.addEventListener('click', (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;

  const toggle = t.closest('[data-vc-menu-toggle]');
  if (toggle) { e.preventDefault(); setMenu(!root.classList.contains('vc-menu-open')); return; }

  const search = t.closest('[data-vc-search]');
  if (search) {
    const modal = document.getElementById('search-modal');
    if (modal && typeof modal.showDialog === 'function') {
      e.preventDefault();
      setMenu(false);
      modal.showDialog();
    }
    return;
  }

  const cart = t.closest('[data-vc-cart]');
  if (cart) {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      e.preventDefault();
      setMenu(false);
      drawer.open();
    }
    return;
  }

  if (t.closest('[data-vc-menu-panel] a')) setMenu(false);

  const atc = t.closest('[data-vc-atc]');
  if (atc) { e.preventDefault(); addToCart(atc); }
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && root.classList.contains('vc-menu-open')) setMenu(false);
});
window.addEventListener('resize', () => {
  if (window.innerWidth > 900 && root.classList.contains('vc-menu-open')) setMenu(false);
  syncHeaderHeight();
}, { passive: true });
window.addEventListener('scroll', onScroll, { passive: true });

/* ---------- Cart count ---------- */
function setCartCount(n) {
  if (typeof n !== 'number' || Number.isNaN(n)) return;
  document.querySelectorAll('[data-vc-cart-count]').forEach((el) => { el.textContent = String(n); });
}
function cartRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}
async function refreshCartCount() {
  try {
    const res = await fetch(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
    const cart = await res.json();
    setCartCount(cart.item_count);
  } catch (_) { /* no-op */ }
}
document.addEventListener('cart:update', (e) => {
  const data = (e && e.detail && e.detail.data) || {};
  if (data.source === 'vc-product-card' && typeof data.cartCount === 'number') setCartCount(data.cartCount);
  else refreshCartCount();
});

/* ---------- Product card add to cart ---------- */
async function addToCart(btn) {
  if (btn.disabled || btn.dataset.added === 'true' || btn.getAttribute('aria-busy') === 'true') return;
  const id = btn.dataset.variantId;
  if (!id) return;
  const label = btn.querySelector('[data-vc-atc-label]') || btn;
  const original = label.textContent;
  btn.setAttribute('aria-busy', 'true');

  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
  });
  const body = new FormData();
  body.append('id', id);
  body.append('quantity', btn.dataset.quantity || '1');
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || cartRoot() + 'cart/add.js';
  try {
    const res = await fetch(addUrl, {
      method: 'POST',
      body,
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add');

    btn.dataset.added = 'true';
    label.textContent = btn.dataset.addedLabel || 'Added';

    let cartCount;
    try {
      const c = await (await fetch(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      cartCount = c.item_count;
    } catch (_) { /* no-op */ }

    const data = {
      source: 'vc-product-card',
      itemCount: cartCount,
      cartCount,
      productId: btn.dataset.productId,
      variantId: id,
      sections: json.sections,
    };
    const evt = Events && Events.CartAddEvent
      ? new Events.CartAddEvent({}, String(id), data)
      : Object.assign(new Event('cart:update', { bubbles: true }), { detail: { resource: {}, sourceId: String(id), data } });
    document.dispatchEvent(evt);
  } catch (err) {
    label.textContent = btn.dataset.errorLabel || 'Unavailable';
    btn.title = err && err.message ? err.message : '';
    setTimeout(() => { label.textContent = original; btn.removeAttribute('title'); }, 2400);
  } finally {
    btn.removeAttribute('aria-busy');
  }
}

/* ---------- Init + theme editor ---------- */
function init() {
  syncOverHero();
  onScroll();
  syncHeaderHeight();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
window.addEventListener('load', syncHeaderHeight);
document.addEventListener('shopify:section:load', init);
document.addEventListener('shopify:section:unload', () => setTimeout(init, 0));
